import { useCallback } from 'react';
import neo4j from 'neo4j-driver';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';

// Updated Profile Query to match your actual Neo4j schema
const PROFILE_QUERY = `
    MATCH (p:PERSON)
    OPTIONAL MATCH (p)-[:MOTIVATED_BY]->(mot:PWG_MOTIVATION)
    OPTIONAL MATCH (p)-[:PREFERS {direction: 'out'}]->(cs_out:PWG_COMM_STYLE)
    OPTIONAL MATCH (p)-[:HAS_RHYTHM]->(ss:PWG_STEP_SIZE)
    WITH p, mot, cs_out, ss,
         CASE 
           WHEN ss.minutes <= 60 THEN '30-60 min blocks'
           ELSE '2-3 hr deep blocks'
         END AS step_size_mapped
    OPTIONAL MATCH (p)-[:WORKS_IN]->(d:PWG_DOMAIN)
    WITH p, mot, cs_out, step_size_mapped, collect(DISTINCT d.name) AS domains
    OPTIONAL MATCH (p)-[hs:HAS_SKILL]->(s:PWG_SKILL)
    WITH p, mot, cs_out, step_size_mapped, domains, collect(DISTINCT {name: s.name, level: hs.level, last_used: toString(hs.last_used), confidence: hs.confidence}) AS skills
    OPTIONAL MATCH (p)-[u:USES]->(t:PWG_TOOL)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, collect(DISTINCT {name: t.name, level: u.level}) AS tools
    OPTIONAL MATCH (p)-[hr:HAS_RHYTHM]->(r:PWG_RHYTHM)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, collect(DISTINCT {label: r.label, interrupt_score: hr.interrupt_score}) as rhythms
    OPTIONAL MATCH (p)-[:PREFERS {direction: 'in'}]->(cs_in:PWG_COMM_STYLE)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, rhythms, collect(DISTINCT cs_in.name) as comms_in
    OPTIONAL MATCH (p)-[:CONSTRAINED_BY]->(c:PWG_CONSTRAINT)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, rhythms, comms_in, collect(DISTINCT c.kind) as constraints
    OPTIONAL MATCH (p)-[:PURSUING]->(g:PWG_GOAL)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, rhythms, comms_in, constraints, collect(DISTINCT {name: g.name, by_date: toString(g.by_date)}) as goals
    OPTIONAL MATCH (p)-[sp:SPEAKS]->(l:Language)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, rhythms, comms_in, constraints, goals, collect(DISTINCT {name: l.code, level: sp.level}) as languages
    OPTIONAL MATCH (p)-[ii:INTERESTED_IN]->(i:Interest)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, rhythms, comms_in, constraints, goals, languages, collect(DISTINCT {name: i.name, intensity: ii.intensity}) as interests
    OPTIONAL MATCH (p)-[:LEADS]->(m:MISSION)
    WITH p, mot, cs_out, step_size_mapped, domains, skills, tools, rhythms, comms_in, constraints, goals, languages, interests, collect(DISTINCT {title: m.title, state: m.state, id: m.id}) as missions_led
    RETURN p.name as name,
           'Game Developer' AS role,
           'Problem statement only' AS finish_line_style,
           step_size_mapped AS step_size,
           coalesce(cs_out.name, 'Brief + checklist') AS comm_out,
           coalesce(mot.name, 'Visible progress') AS motivation,
           'Medium' AS interrupt_tolerance,
           domains, skills, tools, rhythms, comms_in, constraints, goals, languages, interests, missions_led
`;

function calculateScores(profile) { 
    console.log('Calculating scores for profile:', profile);
    
    const scores = {};
    
    // Planning Style - base on skills and domains to create variety
    const skillCount = profile.skills ? profile.skills.length : 0;
    const hasUISkills = profile.skills?.some(s => (s.name && s.name.toLowerCase().includes('ui')) || (s.name && s.name.toLowerCase().includes('design')));
    const hasSystemsSkills = profile.skills?.some(s => (s.name && s.name.toLowerCase().includes('system')) || (s.name && s.name.toLowerCase().includes('architect')));
    
    if (hasUISkills) {
        scores.planning = 6; // UI designers prefer more structured approaches
    } else if (hasSystemsSkills) {
        scores.planning = 9; // Systems people like open problems
    } else if (skillCount > 4) {
        scores.planning = 8; // Experienced developers prefer autonomy
    } else {
        scores.planning = 5; // Default to structured approach
    }
    
    // Work Cadence mapping - from actual step_size data
    scores.cadence = ({ 
        "30-60 min blocks": 4, 
        "2-3 hr deep blocks": 9 
    })[profile.step_size] || 6;
    
    // Communication mapping - from actual comm style data
    const commStyle = profile.comm_out;
    scores.comm = ({ 
        "Quick voice/text summary": 2,
        "quick_voice_text_summary": 2,
        "Brief + checklist": 5,
        "brief_checklist": 5,
        "Artifact-first": 9,
        "artifact_first": 9
    })[commStyle] || 5;
    
    // Primary Focus - base on actual skills
    const hasGameDev = profile.skills?.some(s => 
        (s.name && s.name.toLowerCase().includes('game')) || 
        (s.name && s.name.toLowerCase().includes('unity')) || 
        (s.name && s.name.toLowerCase().includes('unreal'))
    );
    const hasServerSkills = profile.skills?.some(s => 
        (s.name && s.name.toLowerCase().includes('server')) || 
        (s.name && s.name.toLowerCase().includes('network')) || 
        (s.name && s.name.toLowerCase().includes('database'))
    );
    const hasAISkills = profile.skills?.some(s => 
        (s.name && s.name.toLowerCase().includes('ai')) || 
        (s.name && s.name.toLowerCase().includes('machine learning')) || 
        (s.name && s.name.toLowerCase().includes('pathfinding'))
    );
    
    if (hasAISkills) {
        scores.focus = 8; // AI work requires deep technical focus
    } else if (hasServerSkills) {
        scores.focus = 7; // Server work is systems-focused
    } else if (hasGameDev) {
        scores.focus = 5; // Game dev balances technical and creative
    } else if (hasUISkills) {
        scores.focus = 3; // UI work is more user-focused
    } else {
        scores.focus = 4; // Default
    }
    
    // Initiation Driver - from actual motivation data
    scores.driver = ({ 
        "Deadlines": 2, 
        "Fast feedback": 6, 
        "Visible progress": 7, 
        "Autonomy": 9 
    })[profile.motivation] || 7;
    
    // Focus Depth - base on skill complexity and interrupt tolerance from rhythms
    const hasComplexSkills = profile.skills?.some(s => 
        (s.name && s.name.toLowerCase().includes('ai')) || 
        (s.name && s.name.toLowerCase().includes('system')) || 
        (s.name && s.name.toLowerCase().includes('architect')) ||
        (s.name && s.name.toLowerCase().includes('algorithm'))
    );
    const avgInterruptScore = profile.rhythms?.length > 0 ? 
        profile.rhythms.reduce((sum, r) => sum + (r.interrupt_score || 5), 0) / profile.rhythms.length : 5;
    
    if (hasComplexSkills) {
        scores.focusDepth = 8; // Complex work needs deep focus
    } else if (avgInterruptScore < 3) {
        scores.focusDepth = 9; // Low interrupt tolerance = needs deep focus
    } else if (avgInterruptScore > 7) {
        scores.focusDepth = 3; // High interrupt tolerance = can handle distractions
    } else {
        scores.focusDepth = 5; // Medium
    }
    
    // Pace Preference - combine motivation with skill level
    const avgSkillLevel = profile.skills?.length > 0 ? 
        profile.skills.reduce((sum, s) => sum + (s.level || 3), 0) / profile.skills.length : 3;
    
    if (profile.motivation === 'Deadlines') {
        scores.pace = 2; // Deadline-driven = urgency preference
    } else if (profile.motivation === 'Fast feedback') {
        scores.pace = 4; // Feedback-driven = moderate urgency
    } else if (avgSkillLevel >= 4) {
        scores.pace = 7; // High skill = can work at own pace
    } else {
        scores.pace = 5; // Default moderate pace
    }
    
    const finalScores = [scores.planning, scores.cadence, scores.comm, scores.focus, scores.driver, scores.focusDepth, scores.pace];
    console.log('Calculated scores for', profile.name, ':', finalScores);
    
    return finalScores;
}

function getSummary(profile) {
    const skills = profile.skills || [];
    const motivation = profile.motivation || 'Visible progress';
    const stepSize = profile.step_size || '30-60 min blocks';
    const commStyle = profile.comm_out || 'Brief + checklist';
    
    // Analyze skills to determine work style
    const hasUISkills = skills.some(s => (s.name && s.name.toLowerCase().includes('ui')) || (s.name && s.name.toLowerCase().includes('design')));
    const hasServerSkills = skills.some(s => (s.name && s.name.toLowerCase().includes('server')) || (s.name && s.name.toLowerCase().includes('network')));
    const hasAISkills = skills.some(s => (s.name && s.name.toLowerCase().includes('ai')) || (s.name && s.name.toLowerCase().includes('pathfinding')));
    const hasGameSkills = skills.some(s => (s.name && s.name.toLowerCase().includes('game')) || (s.name && s.name.toLowerCase().includes('gameplay')));
    const avgSkillLevel = skills.length > 0 ? skills.reduce((sum, s) => sum + (s.level || 3), 0) / skills.length : 3;
    
    let summary = "";
    
    // Primary work style
    if (hasUISkills && stepSize === '30-60 min blocks') {
        summary = "A detail-oriented designer who excels in iterative, user-focused development cycles.";
    } else if (hasServerSkills && stepSize === '2-3 hr deep blocks') {
        summary = "A systems architect who thrives on deep technical challenges and infrastructure work.";
    } else if (hasAISkills) {
        summary = "A specialized AI developer who needs focused time to tackle complex algorithmic problems.";
    } else if (hasGameSkills && avgSkillLevel >= 4) {
        summary = "An experienced game developer who balances technical expertise with creative problem-solving.";
    } else if (stepSize === '2-3 hr deep blocks' && motivation === 'Autonomy') {
        summary = "A deep-work specialist who produces best results with uninterrupted focus time.";
    } else if (motivation === 'Fast feedback') {
        summary = "A collaborative developer who thrives on rapid iteration and team interaction.";
    } else if (motivation === 'Deadlines') {
        summary = "A results-driven developer who performs well under pressure and clear targets.";
    } else if (skills.length > 4) {
        summary = "A versatile developer with broad expertise across multiple technical domains.";
    } else {
        summary = "A focused team member ready to contribute with their specialized skill set.";
    }
    
    // Add communication style context
    if (commStyle === 'Artifact-first' || commStyle === 'artifact_first') {
        summary += " Prefers documentation and structured communication.";
    } else if (commStyle === 'Quick voice/text summary' || commStyle === 'quick_voice_text_summary') {
        summary += " Works best with quick, direct communication.";
    }
    
    return summary;
}

export function useNeo4j() {
  const { state, dispatch } = useApp();

  const connectToDatabase = useCallback(async (uri, user, password) => {
    dispatch({ type: ActionTypes.SET_CONNECTING, payload: true });
    dispatch({ type: ActionTypes.SET_CONNECTION_STATUS, payload: null });

    try {
      const driver = neo4j.driver(uri, neo4j.auth.basic(user, password), { 
        disableLosslessIntegers: true 
      });
      
      await driver.verifyConnectivity();
      dispatch({ type: ActionTypes.SET_DRIVER, payload: driver });
      return { success: true };
    } catch (error) {
      console.error('Neo4j connection error:', error);
      if (state.driver) {
        await state.driver.close();
      }
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ActionTypes.SET_CONNECTING, payload: false });
    }
  }, [state.driver, dispatch]);

  const fetchUserProfiles = useCallback(async () => {
    if (!state.driver) {
      throw new Error("Database driver not initialized.");
    }

    dispatch({ type: ActionTypes.SET_REFRESHING, payload: true });
    const session = state.driver.session();
    const newUserProfiles = {};

    try {
      const result = await session.run(PROFILE_QUERY);
      
      // Console log the raw Neo4j response for debugging
      console.log('=== NEO4J QUERY RESPONSE ===');
      console.log('Total records found:', result.records.length);
      console.log('Query executed:', PROFILE_QUERY);
      
      result.records.forEach((record, index) => {
        console.log(`\n--- Record ${index + 1} ---`);
        console.log('Raw record keys:', record.keys);
        
        const profileData = {};
        record.keys.forEach(key => {
          const value = record.get(key);
          profileData[key] = value;
          console.log(`${key}:`, value);
        });
        
        // Process the profile data for chart compatibility
        const processedProfile = {
          ...profileData,
          // Map communication style for scoring
          comm_out: profileData.comm_out || 'brief_checklist',
          // Ensure step_size is properly mapped
          step_size: profileData.step_size || '30-60 min blocks',
          // Ensure other fields have defaults
          finish_line_style: profileData.finish_line_style || 'Problem statement only',
          motivation: profileData.motivation || 'Visible progress',
          interrupt_tolerance: profileData.interrupt_tolerance || 'Medium',
          role: profileData.role || 'Game Developer'
        };
        
        console.log('Processed profileData:', processedProfile);
        console.log('Calculated scores:', calculateScores(processedProfile));
        
        newUserProfiles[profileData.name] = {
          scores: calculateScores(processedProfile),
          details: profileData, // Keep original data for display
          summary: getSummary(processedProfile)
        };
      });

      console.log('\n=== FINAL USER PROFILES ===');
      console.log('newUserProfiles:', newUserProfiles);
      console.log('=== END NEO4J DEBUG ===\n');

      dispatch({ type: ActionTypes.SET_USER_PROFILES, payload: newUserProfiles });
      return newUserProfiles;
    } finally {
      await session.close();
      dispatch({ type: ActionTypes.SET_REFRESHING, payload: false });
    }
  }, [state.driver, dispatch]);


  const createUserWithTraits = useCallback(async (formData) => {
    if (!state.driver) {
      throw new Error("Database driver not initialized.");
    }

    const finalName = `${formData.name} ${formData.specialty}`;
    const params = {
      id: `p-${Date.now()}`,
      name: finalName,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      motivation: formData.motivation,
      commStyle: formData.commStyle ? formData.commStyle.toLowerCase().replace(/\s+/g, '_') : 'brief_checklist',
      stepSizeMinutes: formData.stepSize === '30-60 min blocks' ? 45 : 120
    };

    console.log('Creating user with params:', params);

    // Simplified create user query that matches your schema
    const createQuery = `
      // Create the new Person
      CREATE (p:PERSON {id: $id, name: $name, tz: $tz})
      
      // Create or connect to PWG_MOTIVATION
      WITH p
      MERGE (mot:PWG_MOTIVATION {name: $motivation})
      MERGE (p)-[:MOTIVATED_BY]->(mot)
      
      // Create or connect to PWG_COMM_STYLE
      WITH p
      MERGE (cs:PWG_COMM_STYLE {name: $commStyle})
      MERGE (p)-[:PREFERS {direction: 'out'}]->(cs)
      
      // Create or connect to PWG_STEP_SIZE
      WITH p
      MERGE (ss:PWG_STEP_SIZE {minutes: $stepSizeMinutes})
      MERGE (p)-[:HAS_RHYTHM]->(ss)

      RETURN p.name AS name, p.id AS id
    `;

    const session = state.driver.session();
    try {
      console.log('Executing create query:', createQuery);
      const result = await session.run(createQuery, params);
      
      console.log('Create query result:', result);
      console.log('Records returned:', result.records.length);
      
      if (result.records.length === 0) {
        throw new Error('No records returned from create query');
      }
      
      const createdName = result.records[0].get('name');
      const createdId = result.records[0].get('id');
      
      console.log('Successfully created user:', { name: createdName, id: createdId });
      return { success: true, name: createdName };
    } catch (error) {
      console.error("Failed to create user:", error);
      return { success: false, error: error.message };
    } finally {
      await session.close();
    }
  }, [state.driver]);

  const createMission = useCallback(async (missionData) => {
    if (!state.driver) {
      throw new Error("Database driver not initialized.");
    }

    const params = {
      id: `m-${Date.now()}`,
      title: missionData.missionName,
      description: missionData.description || '',
      deadline: missionData.deadline || null,
      assignedPerson: missionData.assignedPerson,
      state: 'Committed',
      createdAt: new Date().toISOString()
    };

    console.log('Creating mission with params:', params);

    // Create mission query that matches your schema
    const createQuery = `
      // Create the new Mission
      CREATE (m:MISSION {
        id: $id,
        title: $title,
        state: $state,
        finish_line: $description,
        window_start: datetime(),
        window_end: CASE 
          WHEN $deadline IS NOT NULL THEN datetime($deadline + 'T23:59:59Z')
          ELSE datetime() + duration('P30D')
        END
      })
      
      // Find the assigned person and create leadership relationship
      WITH m
      MATCH (p:PERSON {name: $assignedPerson})
      MERGE (p)-[:LEADS]->(m)

      RETURN m.id AS mission_id, m.title AS title, p.name AS assigned_to
    `;

    const session = state.driver.session();
    try {
      console.log('Executing create mission query:', createQuery);
      const result = await session.run(createQuery, params);
      
      console.log('Create mission result:', result);
      console.log('Records returned:', result.records.length);
      
      if (result.records.length === 0) {
        throw new Error('No records returned from create mission query - person may not exist');
      }
      
      const missionId = result.records[0].get('mission_id');
      const title = result.records[0].get('title');
      const assignedTo = result.records[0].get('assigned_to');
      
      console.log('Successfully created mission:', { id: missionId, title, assignedTo });
      return { success: true, mission: { id: missionId, title, assignedTo } };
    } catch (error) {
      console.error("Failed to create mission:", error);
      return { success: false, error: error.message };
    } finally {
      await session.close();
    }
  }, [state.driver]);

  const fetchGraphData = useCallback(async () => {
    if (!state.driver) {
      throw new Error("Database driver not initialized.");
    }

    console.log('Fetching graph visualization data...');
    const session = state.driver.session();

    // Comprehensive query to get all nodes and relationships for visualization
    const graphQuery = `
      // Get all persons and their relationships
      MATCH (p:PERSON)
      OPTIONAL MATCH (p)-[r1:LEADS]->(m:MISSION)
      OPTIONAL MATCH (p)-[r2:HAS_SKILL]->(s:PWG_SKILL)
      OPTIONAL MATCH (p)-[r3:USES]->(t:PWG_TOOL)
      OPTIONAL MATCH (p)-[r4:WORKS_IN]->(d:PWG_DOMAIN)
      OPTIONAL MATCH (p)-[r5:MOTIVATED_BY]->(mot:PWG_MOTIVATION)
      OPTIONAL MATCH (p)-[r6:HAS_RHYTHM]->(rhy:PWG_RHYTHM)
      OPTIONAL MATCH (p)-[r7:HAS_RHYTHM]->(ss:PWG_STEP_SIZE)
      OPTIONAL MATCH (p)-[r8:PREFERS]->(cs:PWG_COMM_STYLE)
      OPTIONAL MATCH (p)-[r9:CONSTRAINED_BY]->(con:PWG_CONSTRAINT)
      OPTIONAL MATCH (p)-[r10:PURSUING]->(g:PWG_GOAL)
      OPTIONAL MATCH (p)-[r11:SPEAKS]->(l:Language)
      OPTIONAL MATCH (p)-[r12:INTERESTED_IN]->(i:Interest)
      
      // Collect all nodes and relationships
      WITH p, m, s, t, d, mot, rhy, ss, cs, con, g, l, i, 
           r1, r2, r3, r4, r5, r6, r7, r8, r9, r10, r11, r12
      
      RETURN 
        // Person data
        p.id as person_id, p.name as person_name, 'PERSON' as person_type,
        
        // Mission data
        collect(DISTINCT {id: m.id, title: m.title, state: m.state, type: 'MISSION'}) as missions,
        
        // Skill data  
        collect(DISTINCT {id: s.name, name: s.name, type: 'PWG_SKILL', level: r2.level}) as skills,
        
        // Tool data
        collect(DISTINCT {id: t.name, name: t.name, type: 'PWG_TOOL', level: r3.level}) as tools,
        
        // Domain data
        collect(DISTINCT {id: d.name, name: d.name, type: 'PWG_DOMAIN'}) as domains,
        
        // Motivation data
        collect(DISTINCT {id: mot.name, name: mot.name, type: 'PWG_MOTIVATION'}) as motivations,
        
        // Rhythm data
        collect(DISTINCT {id: rhy.label, name: rhy.label, type: 'PWG_RHYTHM', interrupt_score: r6.interrupt_score}) as rhythms,
        
        // Step Size data
        collect(DISTINCT {id: ss.minutes, name: ss.minutes + ' min blocks', type: 'PWG_STEP_SIZE'}) as step_sizes,
        
        // Communication Style data
        collect(DISTINCT {id: cs.name, name: cs.name, type: 'PWG_COMM_STYLE', direction: r8.direction}) as comm_styles,
        
        // Constraint data
        collect(DISTINCT {id: con.kind, name: con.kind, type: 'PWG_CONSTRAINT'}) as constraints,
        
        // Goal data
        collect(DISTINCT {id: g.name, name: g.name, type: 'PWG_GOAL', by_date: toString(g.by_date)}) as goals,
        
        // Language data
        collect(DISTINCT {id: l.code, name: l.code, type: 'Language', level: r11.level}) as languages,
        
        // Interest data
        collect(DISTINCT {id: i.name, name: i.name, type: 'Interest', intensity: r12.intensity}) as interests
    `;

    try {
      const result = await session.run(graphQuery);
      console.log('Raw graph query result:', result);

      const nodes = [];
      const edges = [];
      const nodeIds = new Set();

      result.records.forEach(record => {
        const personId = record.get('person_id');
        const personName = record.get('person_name');

        // Add person node
        if (personId && !nodeIds.has(personId)) {
          nodes.push({
            id: personId,
            label: personName,
            color: { background: '#3B82F6', border: '#1D4ED8' }, // Blue for persons
            group: 'person'
          });
          nodeIds.add(personId);
        }

        // Add mission nodes and edges
        const missions = record.get('missions').filter(m => m.id);
        missions.forEach(mission => {
          if (!nodeIds.has(mission.id)) {
            nodes.push({
              id: mission.id,
              label: mission.title,
              color: { background: '#8B5CF6', border: '#7C3AED' }, // Purple for missions
              group: 'mission',
              size: 25 // Make missions slightly larger than default (20)
            });
            nodeIds.add(mission.id);
          }
          // Add LEADS relationship
          edges.push({
            from: personId,
            to: mission.id,
            label: 'LEADS',
            color: { color: '#8B5CF6' }
          });
        });

        // Add skill nodes and edges
        const skills = record.get('skills').filter(s => s.id);
        skills.slice(0, 3).forEach(skill => { // Limit to top 3 skills to avoid clutter
          const skillId = `skill_${skill.id}`;
          if (!nodeIds.has(skillId)) {
            nodes.push({
              id: skillId,
              label: `${skill.name}\n(L${skill.level})`,
              color: { background: '#10B981', border: '#059669' }, // Green for skills
              group: 'skill',
              size: 15
            });
            nodeIds.add(skillId);
          }
          edges.push({
            from: personId,
            to: skillId,
            label: 'HAS_SKILL',
            color: { color: '#10B981' }
          });
        });

        // Add domain nodes and edges
        const domains = record.get('domains').filter(d => d.id);
        domains.forEach(domain => {
          const domainId = `domain_${domain.id}`;
          if (!nodeIds.has(domainId)) {
            nodes.push({
              id: domainId,
              label: domain.name,
              color: { background: '#F59E0B', border: '#D97706' }, // Yellow for domains
              group: 'domain',
              size: 18
            });
            nodeIds.add(domainId);
          }
          edges.push({
            from: personId,
            to: domainId,
            label: 'WORKS_IN',
            color: { color: '#F59E0B' }
          });
        });

        // Add motivation nodes and edges
        const motivations = record.get('motivations').filter(m => m.id);
        motivations.forEach(motivation => {
          const motId = `motivation_${motivation.id}`;
          if (!nodeIds.has(motId)) {
            nodes.push({
              id: motId,
              label: motivation.name,
              color: { background: '#EF4444', border: '#DC2626' }, // Red for motivations
              group: 'motivation',
              size: 15
            });
            nodeIds.add(motId);
          }
          edges.push({
            from: personId,
            to: motId,
            label: 'MOTIVATED_BY',
            color: { color: '#EF4444' }
          });
        });

        // Add rhythm nodes and edges
        const rhythms = record.get('rhythms').filter(r => r.id);
        rhythms.forEach(rhythm => {
          const rhythmId = `rhythm_${rhythm.id}`;
          if (!nodeIds.has(rhythmId)) {
            nodes.push({
              id: rhythmId,
              label: `${rhythm.name}\n(${rhythm.interrupt_score})`,
              color: { background: '#A855F7', border: '#7C2D92' }, // Purple for rhythms
              group: 'rhythm',
              size: 12
            });
            nodeIds.add(rhythmId);
          }
          edges.push({
            from: personId,
            to: rhythmId,
            label: 'HAS_RHYTHM',
            color: { color: '#A855F7' }
          });
        });

        // Add communication style nodes and edges
        const commStyles = record.get('comm_styles').filter(cs => cs.id);
        commStyles.forEach(commStyle => {
          const csId = `comm_${commStyle.id}`;
          if (!nodeIds.has(csId)) {
            nodes.push({
              id: csId,
              label: `${commStyle.name}\n(${commStyle.direction})`,
              color: { background: '#06B6D4', border: '#0891B2' }, // Cyan for comm styles
              group: 'communication',
              size: 12
            });
            nodeIds.add(csId);
          }
          edges.push({
            from: personId,
            to: csId,
            label: 'PREFERS',
            color: { color: '#06B6D4' }
          });
        });

        // Add constraint nodes and edges
        const constraints = record.get('constraints').filter(c => c.id);
        constraints.forEach(constraint => {
          const conId = `constraint_${constraint.id}`;
          if (!nodeIds.has(conId)) {
            nodes.push({
              id: conId,
              label: constraint.name.replace(/_/g, ' '),
              color: { background: '#DC2626', border: '#991B1B' }, // Dark red for constraints
              group: 'constraint',
              size: 10
            });
            nodeIds.add(conId);
          }
          edges.push({
            from: personId,
            to: conId,
            label: 'CONSTRAINED_BY',
            color: { color: '#DC2626' }
          });
        });

        // Add goal nodes and edges
        const goals = record.get('goals').filter(g => g.id);
        goals.forEach(goal => {
          const goalId = `goal_${goal.id}`;
          if (!nodeIds.has(goalId)) {
            nodes.push({
              id: goalId,
              label: `${goal.name}\n(by ${goal.by_date?.split('T')[0] || 'TBD'})`,
              color: { background: '#059669', border: '#047857' }, // Dark green for goals
              group: 'goal',
              size: 14
            });
            nodeIds.add(goalId);
          }
          edges.push({
            from: personId,
            to: goalId,
            label: 'PURSUING',
            color: { color: '#059669' }
          });
        });

        // Add language nodes and edges
        const languages = record.get('languages').filter(l => l.id);
        languages.forEach(language => {
          const langId = `lang_${language.id}`;
          if (!nodeIds.has(langId)) {
            nodes.push({
              id: langId,
              label: `${language.name}\n(${language.level})`,
              color: { background: '#7C3AED', border: '#5B21B6' }, // Violet for languages
              group: 'language',
              size: 10
            });
            nodeIds.add(langId);
          }
          edges.push({
            from: personId,
            to: langId,
            label: 'SPEAKS',
            color: { color: '#7C3AED' }
          });
        });

        // Add interest nodes and edges
        const interests = record.get('interests').filter(i => i.id);
        interests.forEach(interest => {
          const intId = `interest_${interest.id}`;
          if (!nodeIds.has(intId)) {
            nodes.push({
              id: intId,
              label: `${interest.name}\n(${interest.intensity}/5)`,
              color: { background: '#F59E0B', border: '#D97706' }, // Orange for interests
              group: 'interest',
              size: 11
            });
            nodeIds.add(intId);
          }
          edges.push({
            from: personId,
            to: intId,
            label: 'INTERESTED_IN',
            color: { color: '#F59E0B' }
          });
        });
      });

      console.log('Processed graph data:', { nodes: nodes.length, edges: edges.length });
      console.log('Nodes:', nodes);
      console.log('Edges:', edges);

      return { nodes, edges };

    } finally {
      await session.close();
    }
  }, [state.driver]);

  return {
    connectToDatabase,
    fetchUserProfiles,
    createUserWithTraits,
    createMission,
    fetchGraphData,
    isConnecting: state.isConnecting,
    isRefreshing: state.isRefreshing,
    driver: state.driver
  };
}
